import { useTranslation } from 'react-i18next';

import type { BookingEventRegistration } from '@/types';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

import {
  BOOKING_CHANNEL_LABEL_KEYS,
  BOOKING_CLOSED_STATUSES,
  BOOKING_PAYMENT_LABEL_KEYS,
} from '@/constants';

import { BookingPersonName } from '../BookingPersonName';
import { BookingStatusBadges } from '../BookingStatusBadges';

type RegistrationsListProps = {
  registrations: BookingEventRegistration[];
  waveNames: Map<number, string>;
  isPending: boolean;
  onAttend: (registrationId: number) => void;
  onMarkMissed: (registrationId: number) => void;
  onCancel: (registrationId: number) => void;
};

export const RegistrationsList = (props: RegistrationsListProps) => {
  const {
    registrations,
    waveNames,
    isPending,
    onAttend,
    onMarkMissed,
    onCancel,
  } = props;
  const { t } = useTranslation();

  if (registrations.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">{t('No registrations')}</p>
    );
  }

  return (
    <div className="grid gap-3">
      {registrations.map((registration) => {
        const isClosed = BOOKING_CLOSED_STATUSES.includes(registration.status);
        const isMissable = registration.status === 'reserved';
        const waveName =
          registration.event_wave_id === null
            ? null
            : waveNames.get(registration.event_wave_id);

        return (
          <div
            key={registration.id}
            className="grid gap-2 rounded-md border p-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <BookingPersonName
                client={registration.client}
                guestInfo={registration.guest_info}
                className="text-sm font-medium"
              />

              <BookingStatusBadges
                status={registration.status}
                isUnassigned={registration.unassigned}
              />
            </div>

            <div className="text-muted-foreground grid gap-0.5 text-xs">
              {!!waveName && <span>{`${t('Wave')}: ${waveName}`}</span>}
              <span>
                {`${t('Channel')}: ${t(BOOKING_CHANNEL_LABEL_KEYS[registration.channel])}`}
              </span>
              {registration.payment_status !== 'none' && (
                <span>
                  {`${t('Payment')}: ${t(BOOKING_PAYMENT_LABEL_KEYS[registration.payment_status])}`}
                </span>
              )}
              {registration.custom_field_answers.map((answer) => (
                <span key={answer.id}>
                  {`${answer.label}: ${answer.value || '—'}`}
                </span>
              ))}
              {!!registration.note && (
                <span className="whitespace-pre-line">
                  {`${t('Note')}: ${registration.note}`}
                </span>
              )}
              {!!registration.staff_comment && (
                <span className="whitespace-pre-line">
                  {`${t('Specialist comment')}: ${registration.staff_comment}`}
                </span>
              )}
            </div>

            {!isClosed && (
              <>
                <Separator />

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => onAttend(registration.id)}
                    disabled={isPending}
                  >
                    {t('Attendance')}
                  </Button>

                  {isMissable && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onMarkMissed(registration.id)}
                      disabled={isPending}
                    >
                      {t('Mark no-show')}
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onCancel(registration.id)}
                    disabled={isPending}
                  >
                    {t('Cancel registration')}
                  </Button>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
